import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const CategoryPickerModal = ({ open, onSaved }) => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/student/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [open]);

  const pick = async (name) => {
    setSaving(true);
    try {
      const { data } = await api.post("/student/profile/category", { name });
      toast.success(`You're now set to "${data.user.category}".`);
      onSaved(data.user);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const submitCustom = (e) => {
    e.preventDefault();
    if (!custom.trim()) return;
    pick(custom.trim());
  };

  const approved = cats.filter((c) => c.status === "approved");
  const myPending = cats.filter((c) => c.status === "pending");

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-lg" data-testid="student-category-picker" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="h-10 w-10 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] grid place-items-center mb-2">
            <BookOpen className="h-5 w-5" />
          </div>
          <DialogTitle className="font-heading text-2xl">What are you preparing for?</DialogTitle>
          <DialogDescription>
            Pick your exam category — you'll only see mock tests for your chosen stream. You can also propose a new one; admin will approve it for everyone else.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="category-picker-options">
              {approved.map((c) => (
                <button key={c.id} type="button" disabled={saving} onClick={() => pick(c.name)} data-testid={`category-option-${c.name}`}
                  className="px-4 py-2 rounded-full border border-slate-200 text-sm font-medium hover:border-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#1D4ED8] transition-colors disabled:opacity-50">
                  {c.name}
                </button>
              ))}
            </div>

            {myPending.length > 0 && (
              <div className="mt-3 text-xs text-slate-500">
                Your proposed:
                {myPending.map((c) => (
                  <button key={c.id} type="button" disabled={saving} onClick={() => pick(c.name)} className="ml-2 inline-flex items-center">
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{c.name} · awaiting approval</Badge>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-5 border-t border-slate-200 pt-5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Don't see yours?</Label>
              <form onSubmit={submitCustom} className="mt-2 flex gap-2">
                <Input value={custom} onChange={(e) => setCustom(e.target.value)} maxLength={40} placeholder="Type a new category (e.g. GATE)" data-testid="category-custom-input" />
                <Button type="submit" disabled={saving || !custom.trim()} data-testid="category-propose-button" className="bg-slate-900 hover:bg-[#2563EB] hover:text-white text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Propose</>}
                </Button>
              </form>
              <p className="text-xs text-slate-400 mt-2">A new category gets attached to your account right away. Other students will see it once the admin approves it.</p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
